import { feedKey } from '../lib/feedContext';
/**
 * Калькулятор на полке: одна вилка сверху, пока сам не отпустишь.
 *
 * Это не избранное и не пауза. Клик по вилке или «в калькулятор» кладёт её
 * в стол сверху — сам калькулятор, не копию строки. Тестер (19.09, Сергей):
 * как у Positivebet — стол всегда открыт, вилка подставилась, держится,
 * даже мёртвая. Стопку не собираем.
 *
 * Закрытие карточки полку не снимает. Ушла из кадра — пишем «вилки нет» и
 * оставляем последние числа. Вернулась тем же матчем и теми же конторами —
 * полка переезжает на живой id, карточку события не закрываем.
 *
 * Фильтры закреплённую не снимают: ради этого её и жмут.
 */

import { useEffect, useSyncExternalStore } from 'react';

import { API_BASE } from '../lib/build';
import { dropHeldPromises } from './act';
import { carryPages, dropPages } from './liveQuote';
import { holdMore, useScreen } from './store';

/** Одна. Вторая вытесняет первую — мусор в кучу не собираем. */
export const CLIP_MAX = 1;

const CONTOUR = API_BASE === '' ? 'v1' : API_BASE.replace(/[^a-z0-9]+/gi, '') || 'alt';

/** Ключ хранилища. Открыт наружу ради тестов. */
export const clipKey = (): string => feedKey(`forq.clips.${CONTOUR}`);

const NONE: readonly string[] = [];

const listeners = new Set<() => void>();

interface ClipRec {
  id: string;
  ev: string;
  sources: string;
}

let recs: ClipRec[] = read();
let idsSnap: readonly string[] = recs.length === 0 ? NONE : recs.map((rec) => rec.id);

holdMore('clip', () => idsSnap);

function asRec(raw: unknown): ClipRec | null {
  if (typeof raw === 'string' && raw !== '') return { id: raw, ev: '', sources: '' };
  if (raw === null || typeof raw !== 'object') return null;
  const id = 'id' in raw ? raw.id : null;
  if (typeof id !== 'string' || id === '') return null;
  const ev = 'ev' in raw && typeof raw.ev === 'string' ? raw.ev : '';
  const sources = 'sources' in raw && typeof raw.sources === 'string' ? raw.sources : '';
  return { id, ev, sources };
}

function read(): ClipRec[] {
  try {
    const raw = localStorage.getItem(clipKey());
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [];
    const out: ClipRec[] = [];
    const seen = new Set<string>();
    for (const one of list) {
      const rec = asRec(one);
      if (rec === null || seen.has(rec.id)) continue;
      seen.add(rec.id);
      out.push(rec);
      if (out.length >= CLIP_MAX) break;
    }
    return out;
  } catch {
    return [];
  }
}

function sameRecs(left: readonly ClipRec[], right: readonly ClipRec[]): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (rec, i) =>
      rec.id === right[i]!.id && rec.ev === right[i]!.ev && rec.sources === right[i]!.sources,
  );
}

function write(next: ClipRec[]): void {
  recs = next;
  idsSnap = recs.length === 0 ? NONE : recs.map((rec) => rec.id);
  try {
    localStorage.setItem(clipKey(), JSON.stringify(recs));
  } catch {
    /* хранилище вкладки может быть закрыто — полка живёт до перезагрузки */
  }
  for (const listen of listeners) listen();
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
}

/** Текущий список: первая — верх полки. */
export const clipIds = (): readonly string[] => idsSnap;

export const isClipped = (id: string): boolean => recs.some((rec) => rec.id === id);

function sourcesKey(fork: { legs?: readonly { s?: string }[] }): string {
  return (fork.legs ?? [])
    .map((leg) => leg.s ?? '')
    .filter((code) => code !== '')
    .sort()
    .join('\0');
}

function recOf(fork: { id: string; ev?: string; legs?: readonly { s?: string }[] }): ClipRec {
  return { id: fork.id, ev: fork.ev ?? '', sources: sourcesKey(fork) };
}

function placeClip(id: string): void {
  const was = recs.map((rec) => rec.id);
  if (recs.some((rec) => rec.id === id)) return;
  const live = useScreen.getState().forks.get(id);
  for (const old of was) {
    if (old === id) continue;
    dropHeldPromises(old);
  }
  write([
    {
      id,
      ev: live?.ev ?? '',
      sources: live === undefined ? '' : sourcesKey(live),
    },
  ]);
  for (const old of was) {
    if (old === id) continue;
    useScreen.getState().releaseLocks(old);
    dropPages(old);
  }
}

/** В калькулятор или закрыл. Новая вытесняет ту, что держали. */
export function toggleClip(id: string): void {
  if (id === '') return;
  if (recs.some((rec) => rec.id === id)) {
    dropHeldPromises(id);
    write([]);
    useScreen.getState().releaseLocks(id);
    dropPages(id);
    return;
  }
  placeClip(id);
}

/**
 * Клик по вилке — в стол. Повтор не снимает: снимает только «убрать».
 * Стол как у Positivebet: всегда открыт, вилка подставляется.
 */
export function putClip(id: string): void {
  if (id === '') return;
  placeClip(id);
}

/**
 * Живая вилка с тем же матчем и теми же конторами занимает полку.
 * Карточку не трогаем: человек в событии — из него не выбиваем.
 */
export function bindClips(
  forks: ReadonlyMap<string, { id: string; ev?: string; legs?: readonly { s?: string }[] }>,
): void {
  if (recs.length === 0) return;
  const next: ClipRec[] = [];
  for (const rec of recs) {
    const live = forks.get(rec.id);
    if (live !== undefined) {
      next.push(recOf(live));
      continue;
    }
    if (rec.ev === '') {
      next.push(rec);
      continue;
    }
    let found: ClipRec | null = null;
    for (const fork of forks.values()) {
      if ((fork.ev ?? '') === rec.ev && sourcesKey(fork) === rec.sources) {
        found = recOf(fork);
        break;
      }
    }
    next.push(found ?? rec);
  }
  if (sameRecs(recs, next)) return;
  const was = recs.map((rec) => rec.id);
  write(next);
  const screen = useScreen.getState();
  next.forEach((rec, i) => {
    const old = was[i];
    if (old === undefined || old === rec.id) return;
    screen.moveLocks(old, rec.id);
    const from = screen.ghosts.get(old)?.fork;
    const to = screen.forks.get(rec.id);
    if (from !== undefined && to !== undefined) carryPages(from, to);
  });
}

/**
 * Раньше снимали скрепку, когда вилки не стало в кадре. Тестер (18.09):
 * полка — калькулятор, и её отпускают только сами. Функция осталась, чтобы
 * старый вызов не вычистил цифры в тот момент, ради которого вилку держали.
 */
export function pruneClips(_live: ReadonlySet<string>): void {}

/** Тесты: вернуть пустую полку и вычистить хранилище. */
export function resetClips(): void {
  try {
    localStorage.removeItem(clipKey());
  } catch {
    /* пусто */
  }
  for (const rec of recs) dropPages(rec.id);
  recs = [];
  idsSnap = NONE;
  for (const listen of listeners) listen();
}

/** Тесты: прочитать полку заново, как после перезагрузки вкладки. */
export function hydrateClips(): void {
  recs = read();
  idsSnap = recs.length === 0 ? NONE : recs.map((rec) => rec.id);
  for (const listen of listeners) listen();
}

/**
 * Разнять кадр на полку и остаток. Порядок полки — порядок `ids`, не денег.
 * Чего в кадре нет, на полку не встаёт: пустая кнопка без строки хуже тишины.
 */
export function shelf<T extends { id: string }>(
  rows: readonly T[],
  pinned: readonly string[],
): { pinned: T[]; rest: T[] } {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const top: T[] = [];
  const taken = new Set<string>();
  for (const id of pinned) {
    const row = byId.get(id);
    if (row === undefined) continue;
    top.push(row);
    taken.add(id);
  }
  return { pinned: top, rest: rows.filter((row) => !taken.has(row.id)) };
}

/** Подписка для экрана: список меняется только от нажатия и от переезда id. */
export function useClips(): readonly string[] {
  return useSyncExternalStore(subscribe, clipIds, () => NONE);
}

/**
 * Подтянуть живой id на полку. Имя старое: сито уже зовёт этот хук, а файл
 * сита чужой — сигнатуру не трогаем.
 */
export function useClipPrune(): void {
  const forks = useScreen((s) => s.forks);
  const pinned = useClips();
  useEffect(() => {
    bindClips(forks);
  }, [forks, pinned]);
}
